import { useLayoutEffect, useRef, type RefObject } from "react";

interface Point {
  id: string;
  offset: number;
}

/** Raw thread lists have no virtualizer to preserve their reading point. */
export function useThreadReadingPosition(
  ref: RefObject<HTMLDivElement | null>,
  key: string,
  replies: readonly { id: string }[],
) {
  const saved = useRef<{ key: string; point: Point | null }>({ key, point: null });
  useLayoutEffect(() => {
    const parent = ref.current;
    if (!parent) return;
    if (saved.current.key !== key) saved.current = { key, point: null };
    const rows = () => [...parent.querySelectorAll<HTMLElement>("[data-thread-reply]")];
    const capture = () => {
      const top = parent.getBoundingClientRect().top;
      const row = rows().find(
        (candidate) =>
          !candidate.hidden &&
          candidate.getBoundingClientRect().bottom > top &&
          candidate.getBoundingClientRect().top < top + parent.clientHeight,
      );
      saved.current.point = row
        ? { id: row.dataset.threadReply ?? "", offset: row.getBoundingClientRect().top - top }
        : null;
    };
    const restore = () => {
      const point = saved.current.point;
      if (point) {
        const row = rows().find(
          (candidate) => candidate.dataset.threadReply === point.id && !candidate.hidden,
        );
        if (row)
          parent.scrollTop +=
            row.getBoundingClientRect().top - parent.getBoundingClientRect().top - point.offset;
      }
      capture();
    };
    restore();
    parent.addEventListener("scroll", capture, { passive: true });
    // Rows resizing (media, previews) and controls appearing or leaving
    // (load-earlier) both move the reader; either one restores the point.
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(restore);
    const observeRows = () => {
      observer?.disconnect();
      for (const child of parent.children) observer?.observe(child);
      for (const row of rows()) observer?.observe(row);
    };
    observeRows();
    const mutations =
      typeof MutationObserver === "undefined"
        ? null
        : new MutationObserver(() => {
            observeRows();
            restore();
          });
    mutations?.observe(parent, { childList: true, subtree: true });
    return () => {
      observer?.disconnect();
      mutations?.disconnect();
      parent.removeEventListener("scroll", capture);
    };
  }, [ref, key, replies]);
}
